<template>
  <div class="card-container">
    <div class="header-actions">
      <button class="back-btn" @click="handleBack" title="Go back">
        <a-icon type="arrow-left" />
      </button>
      <div class="muic-badge">
        <a-icon type="safety-certificate" /> {{ accountType }}
      </div>
    </div>

    <p class="room-text">Orientation Check-in</p>

    <h1 class="main-title">
      Check in. You're in the draw.
    </h1>

    <p class="description">
      Sign in with your MUIC Microsoft account. Once verified, your
      attendance and prize entry are confirmed.
    </p>

    <div class="steps-container">
      <div class="step-box border-bottom">
        <div class="step-circle">1</div>
        <div class="step-content">
          <div class="step-title">Sign in</div>
          <div class="step-desc">Use MUIC Account</div>
        </div>
      </div>
      <div class="step-box bg-light">
        <div class="step-circle">2</div>
        <div class="step-content">
          <div class="step-title">Done</div>
          <div class="step-desc">Get one prize-draw entry</div>
        </div>
      </div>
    </div>

    <button class="msal-btn" @click="handleLogin" :disabled="loading">
      <svg class="ms-logo" xmlns="http://www.w3.org/2000/svg" width="21" height="21" viewBox="0 0 21 21">
        <rect x="1" y="1" width="9" height="9" fill="#f25022" />
        <rect x="11" y="1" width="9" height="9" fill="#7fba00" />
        <rect x="1" y="11" width="9" height="9" fill="#00a4ef" />
        <rect x="11" y="11" width="9" height="9" fill="#ffb900" />
      </svg>
      <span v-if="!loading">Continue with MUIC Account</span>
      <a-spin v-else size="small" style="margin-left: 10px; color: white;"></a-spin>
    </button>
    <div class="btn-subtext">Use MUIC account</div>
  </div>
</template>

<script>
import { PublicClientApplication } from "@azure/msal-browser";
import axios from 'axios';

export default {
  name: 'CheckInCard',
  props: {
    accountType: {
      type: String,
      default: 'MUIC Account'
    },
    roomId: {
      type: String,
      default: ''
    },
    providerConfig: {
      type: Object,
      default: null
    }
  },
  data() {
    return {
      loading: false,
      msalInstance: null,
    };
  },
  created() {
    this.initMsal();
  },
  watch: {
    providerConfig() {
      this.initMsal();
    }
  },
  methods: {
    initMsal() {
      if (!this.providerConfig) return;
      const msalConfig = {
        auth: {
          clientId: this.providerConfig.public_client_id,
          authority: this.providerConfig.authority,
          redirectUri: window.location.origin + "/orientation"
        }
      };
      this.msalInstance = new PublicClientApplication(msalConfig);
    },
    async handleLogin() {
      if (!this.msalInstance) {
        this.$message.error('Configuration not loaded properly.');
        return;
      }
      this.loading = true;
      try {
        await this.msalInstance.initialize();
        const loginRequest = {
          scopes: [this.providerConfig.scope],
        };
        const response = await this.msalInstance.loginPopup(loginRequest);

        // Log MSAL Configuration keys directly
        console.log(`[MSAL INFO] Client ID used: ${this.providerConfig.public_client_id}`);
        console.log(`[MSAL INFO] Authority (Tenant ID) used: ${this.providerConfig.authority}`);

        console.log("Orientation Login Success:", response);

        // Fetch /me/ for both account types
        let checkInData = null;
        if (this.roomId) {
          try {
            const meResponse = await axios.get(`/orientation-game/api/v1/public/rooms/${this.roomId}/me/`, {
              headers: {
                Authorization: `Bearer ${response.accessToken}`
              }
            });
            console.log("GET /me/ Response:", meResponse.data);

            if (meResponse.data && meResponse.data.checked_in === true) {
              console.log("Already checked in, skipping POST check-in.");
              checkInData = meResponse.data.check_in;
            } else {
              // Post to backend with MSAL accessToken (Only for MUIC Account)
              if (this.accountType === 'MUIC Account') {
                await axios.post(`/orientation-game/api/v1/public/rooms/${this.roomId}/check-in/`, {}, {
                  headers: {
                    Authorization: `Bearer ${response.accessToken}`
                  }
                });
                console.log("POST Check-in Success (MUIC)");
              }
            }
          } catch (apiError) {
            console.error("API Error (Check-in or /me/):", apiError);
            if (apiError.response) {
              const errData = apiError.response.data || {};
              const detail = errData.detail || "Student ID is not found on the active event roster.";
              this.$message.error(detail, 5);
            } else {
              this.$message.error("Check-in failed. Please try again.", 5);
            }
            await this.handleBack();
            return;
          }
        }

        // Emit success with the account details, access token, and checkIn data
        this.$message.success('Check-in successful!');
        this.$emit('success', { account: response.account, accessToken: response.accessToken, checkInData });
      } catch (error) {
        console.error("Orientation Login Error:", error);
        this.$message.error('Check-in failed or was cancelled.');
      } finally {
        this.loading = false;
      }
    },
    async handleBack() {
      this.loading = true;
      try {
        await this.msalInstance.initialize();
        const accounts = this.msalInstance.getAllAccounts();
        if (accounts.length > 0) {
          await this.msalInstance.logoutPopup({
            account: accounts[0],
            postLogoutRedirectUri: window.location.origin + "/orientation"
          });
        }
      } catch (e) {
        console.error("Logout Error:", e);
      } finally {
        this.loading = false;
        this.$emit('back');
      }
    }
  }
};
</script>

<style scoped>
/* Centered Card */
.card-container {
  background: white;
  width: 100%;
  max-width: 480px;
  padding: 50px 40px;
  border-radius: 20px;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.05);
  text-align: left;
}

/* Header Actions */
.header-actions {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 20px;
}

.back-btn {
  background: #f0ebf8;
  color: #3e247a;
  border: none;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s ease;
}

.back-btn:hover {
  background: #e1d5f2;
}

.muic-badge {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: #f0ebf8;
  color: #3e247a;
  font-size: 12px;
  font-weight: 700;
  padding: 6px 12px;
  border-radius: 20px;
}

/* Typography */
.room-text {
  color: #555;
  font-size: 14px;
  font-weight: 600;
  margin-bottom: 12px;
}

.main-title {
  color: #3e247a;
  font-size: 54px;
  font-weight: 800;
  line-height: 1.1;
  margin-bottom: 24px;
  letter-spacing: -1.5px;
}

.description {
  color: #6c757d;
  font-size: 14px;
  line-height: 1.6;
  margin-bottom: 30px;
}

/* Steps Box */
.steps-container {
  border: 1px solid #eaeaea;
  border-radius: 12px;
  margin-bottom: 30px;
  overflow: hidden;
}

.step-box {
  display: flex;
  align-items: center;
  padding: 16px 20px;
  gap: 16px;
}

.border-bottom {
  border-bottom: 1px solid #eaeaea;
}

.bg-light {
  background-color: #f9f9fc;
}

.step-circle {
  width: 28px;
  height: 28px;
  border-radius: 50%;
  background: #f0ebf8;
  color: #3e247a;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  font-weight: 700;
}

.step-content {
  display: flex;
  flex-direction: column;
}

.step-title {
  font-size: 14px;
  font-weight: 700;
  color: #212529;
}

.step-desc {
  font-size: 12px;
  color: #6c757d;
}

/* Action Button */
.msal-btn {
  width: 100%;
  background: #472c82;
  color: white;
  border: none;
  padding: 16px;
  border-radius: 10px;
  font-size: 16px;
  font-weight: 600;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  cursor: pointer;
  transition: background 0.2s ease;
}

.msal-btn:hover {
  background: #361f63;
}

.msal-btn:disabled {
  opacity: 0.7;
  cursor: not-allowed;
}

.btn-subtext {
  text-align: center;
  color: #888;
  font-size: 12px;
  margin-top: 12px;
}

/* Responsive adjustments */
@media (max-width: 600px) {
  .card-container {
    padding: 30px 24px;
  }

  .main-title {
    font-size: 40px;
    margin-bottom: 16px;
  }

  .description {
    font-size: 13px;
    margin-bottom: 20px;
  }
}

@media (max-height: 750px) {
  .main-title {
    font-size: 32px;
  }

  .card-container {
    padding: 20px 24px;
  }

  .steps-container {
    margin-bottom: 20px;
  }

  .description {
    margin-bottom: 16px;
  }
}
</style>
